import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { resetPassword } from '../../services/authService';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const navigate = useNavigate();
  const email = localStorage.getItem('resetEmail');

  useEffect(() => {
    if (password.length > 0 && password.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
    } else {
      setPasswordError('');
    }

    if (confirmPassword.length > 0 && password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
    } else {
      setConfirmPasswordError('');
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email!, password);
      localStorage.removeItem('resetEmail');
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.msg || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        background:
          'radial-gradient(circle at top, rgba(90, 75, 252, 0.12), transparent 40%), radial-gradient(circle at bottom, rgba(0, 184, 148, 0.16), transparent 45%), #f4f6fb',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 3,
          boxShadow: '0 36px 60px rgba(15, 23, 42, 0.16)',
        }}
      >
        <CardContent sx={{ p: { xs: 4, md: 5 } }}>
          <Stack spacing={4} component="form" onSubmit={handleSubmit}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Set a new password
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Your password should be unique to ZapTick AI. Keep it secure and memorable.
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack spacing={3}>
              <TextField
                label="New password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                helperText={passwordError}
                error={Boolean(passwordError)}
              />
              <TextField
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                helperText={confirmPasswordError}
                error={Boolean(confirmPasswordError)}
              />
            </Stack>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || Boolean(passwordError) || Boolean(confirmPasswordError)}
            >
              {loading ? 'Updating…' : 'Update password'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
